// Import necessary modules and configurations
import controlSelectorTemplate from './controlSelector.module.html?raw';
import { db, FirebaseTimestamp } from '../../config/main_config';
import { AssetRegister } from '../namespaces/AssetRegister.namespace';
import { RiskRegister } from '../namespaces/RiskRegister.namespace';
import { getEnabledModules, isStandardEnabled } from '../utils/EnabledModulesManager';
import { waitForSelector } from '../utils/waitForSelector';

// Define standard filters for different compliance frameworks
var filters = {
    standards: {
        'ISO 27001': {
            validDocuments: [
                'iso270012022AnnexAControls',
                'iso270012022Controls'
            ],
            valuesToGrab: [
                'controlID&controlID',
                'controlName&controlName',
                'controlDescription&controlDescription',
            ],
        },
        'SOC 2': {
            validDocuments: ['SOC2Controls'],
            valuesToGrab: [
                'controlID&controlID',
                'controlName&controlName',
                'controlDescription&controlDescription',
            ],
        },
        'NIST 800-53': {
            validDocuments: ['nist800-53-rev5'],
            valuesToGrab: [
                'controlID&controlID',
                'controlName&controlName',
                'controlDescription&controlDescription',
            ],
        },
        'Essential 8': {
            validDocuments: ['ism'],
            valuesToGrab: ['controlID&controlID', 'controlName&description', 'controlDescription&topic'],
        },
        'Privacy Act': {
            validDocuments: ['paControls'],
            valuesToGrab: [
                'controlID&controlID',
                'controlName&controlName',
                'controlDescription&controlDescription',
            ],
        },
        'DISP': {
            validDocuments: ['DISPControls'],
            valuesToGrab: ['controlID&controlID', 'controlName&controlDesc', 'controlDescription&DISPCategory'],
        },
        'ISM': {
            validDocuments: ['ism'],
            valuesToGrab: ['controlID&controlID', 'controlName&description', 'controlDescription&topic'],
        },
        'SMB1001': {
            validDocuments: ['SMB1001Controls'],
            valuesToGrab: [
                'controlID&controlID',
                'controlName&controlName',
                'controlDescription&controlDescription',
                'level&level'
            ],
        }
    },
    assets: {
        validDocuments: [
            `asset`,
        ],
        valuesToGrab: [
            'assetID&assetID',
            'assetIDStatus&assetIDStatus',
            'assetIDstatus&assetIDstatus',
            'assetDesc&assetDesc',
            'dateAdded&dateAdded',
        ],
    },
    risks: {
        validDocuments: [
            `risk`,
        ],
        valuesToGrab: [
            'riskID&riskID',
            'risk&risk',
            'dateAdded&dateAdded',
            'riskIDstatus&riskIDstatus',
        ],
    },
    ismsManual: {
        validDocuments: [
            `ismsManual`,
        ],
        mapsToGrab: [
            {
                name: 'legalObligations',
                valuesToGrab: [
                    'requirement&requirement',
                    'dateAdded&dateAdded',
                    'updateMethod&updateMethod'
                ]
            },
            {
                name: 'ismsObjectives',
                valuesToGrab: [
                    'objectiveText&objectiveText',
                    'objectiveTargetDate&objectiveTargetDate',
                    'dateAdded&dateAdded'
                ]
            }
        ],
    }
};

async function loadData() {
    const controls = {};
    const assets = {};
    const risks = {};
    const legalObligations = {};
    const ismsObjectives = {};
    try {
        const querySnapshot = await db.collection('compliance').get();
        const customerComplianceSnapshot = await db.collection('customers').doc(window.companyID).collection('compliance').get();
        const customerKeyDocsCollectionSnapshot = await db.collection('customers').doc(window.companyID).collection('product').doc('policyPackages').collection('ISO27001keyDocuments').get();
        const complianceModulesData = await db.collection("customers").doc(window.companyID).collection('companySettings').doc('complianceModules').get();

        var e8Level1;
        var e8Level2;
        var e8Level3;
        var ismControls;

        if (complianceModulesData.data()?.DISP?.Enabled !== true) {
            delete filters.standards['DISP'];
        }
        if (complianceModulesData.data()?.ism?.Enabled !== true) {
            delete filters.standards['ISM'];
        } else {
            ismControls = await db
                .collection('compliance')
                .doc('ism')
                .collection('controls')
                .get();
        }
        if (!complianceModulesData.data()?.standardsAdded?.length) {
            delete filters.standards['Essential 8'];
        } else {
            e8Level1 = await db
                .collection('compliance')
                .doc('ism')
                .collection('controls')
                .where('ml1', '==', 'Yes')
                .get();
            e8Level2 = await db
                .collection('compliance')
                .doc('ism')
                .collection('controls')
                .where('ml2', '==', 'Yes')
                .get();
            e8Level3 = await db
                .collection('compliance')
                .doc('ism')
                .collection('controls')
                .where('ml3', '==', 'Yes')
                .get();
        }
        if (complianceModulesData.data()?.['nist-800-53']?.Enabled !== true) {
            delete filters.standards['NIST 800-53'];
        }
        if (complianceModulesData.data()?.['SOC2']?.Enabled !== true) {
            delete filters.standards['SOC 2'];
        }
        if (complianceModulesData.data()?.['SMB1001']?.Enabled !== true) {
            delete filters.standards['SMB1001'];
        }

        if (window.customerPlan?.toLowerCase() === 'starter') {
            delete filters.standards['ISO 27001'];
            delete filters.standards['SOC 2'];
            delete filters.standards['NIST 800-53'];
            delete filters.standards['ISM'];
        }

        querySnapshot.forEach((doc) => {
            const docData = doc.data();

            for (const standardKey in filters.standards) {
                let validDocuments = filters.standards[standardKey].validDocuments;

                if (validDocuments.includes(doc.id)) {
                    if (
                        !doc.id.toLowerCase().includes('_backup') &&
                        !doc.id.toLowerCase().includes('e8controls') &&
                        !doc.id.toLowerCase().includes('ism')
                    ) {
                        // console.log('Standard found');
                        // Document ID matches a standard key
                        const standard = filters.standards[standardKey];
                        const valuesToGrab = standard.valuesToGrab;
                        const valuesForResponse = standard.valuesForResponse;

                        if (!controls.hasOwnProperty(standardKey)) {
                            controls[standardKey] = {};
                        }

                        let standardControls;
                        if (doc.id === 'SMB1001Controls') {
                            // For SMB1001, controls are in an array under the "Controls" key
                            standardControls = {};
                            docData.Controls.forEach((control, index) => {
                                standardControls[control.controlID] = control;
                            });
                        } else {
                            standardControls = docData;
                        }

                        for (let i = 0; i < Object.keys(standardControls).sort((a, b) => a.replaceAll("A.", '') - b.replaceAll("A.", '')).length; i++) {
                            // console.log(i);
                            let key = Object.keys(standardControls)[i];
                            let value = standardControls[key];
                            if (!controls[standardKey].hasOwnProperty(key)) {
                                controls[standardKey][key] = {
                                    standard: standardKey,
                                    framework: doc.id,
                                    controlApplicable: true,
                                };
                            }

                            for (let j = 0; j < valuesToGrab.length; j++) {
                                let unprocessedKey = valuesToGrab[j].split('&');
                                let actualKey = unprocessedKey[0];
                                let key2 = unprocessedKey[1];
                                let valueKeys = key2.split('|');
                                let value2 = '';
                                if (value.hasOwnProperty(valueKeys[0])) {
                                    value2 = value[valueKeys[0]];
                                } else if (value.hasOwnProperty(valueKeys[1])) {
                                    value2 = value[valueKeys[1]];
                                }

                                if (!controls[standardKey][key].hasOwnProperty(actualKey)) {
                                    controls[standardKey][key][actualKey] = value2;
                                }
                            }
                        }
                    } else if (doc.id.toLowerCase().includes('e8controls')) {
                        e8Level1.docs.forEach((doc) => {
                            const docData = doc.data();
                            const standard = filters.standards['Essential 8'];
                            const valuesToGrab = standard.valuesToGrab;
                            const valuesForResponse = standard.valuesForResponse;

                            if (!controls.hasOwnProperty('Essential 8')) {
                                controls['Essential 8'] = {};
                            }

                            let standardControls = docData;
                            let key = doc.id;
                            let value = standardControls;
                            if (!controls['Essential 8'].hasOwnProperty(key)) {
                                controls['Essential 8'][key] = {
                                    standard: 'Essential 8',
                                    framework: 'E8Controls',
                                    controlApplicable: true,
                                };
                            }

                            for (let j = 0; j < valuesToGrab.length; j++) {
                                let unprocessedKey = valuesToGrab[j].split('&');
                                let actualKey = unprocessedKey[0];
                                let key2 = unprocessedKey[1];
                                let valueKeys = key2.split('|');
                                let value2 = '';
                                if (value.hasOwnProperty(valueKeys[0])) {
                                    value2 = value[valueKeys[0]];
                                } else if (value.hasOwnProperty(valueKeys[1])) {
                                    value2 = value[valueKeys[1]];
                                }

                                if (!controls['Essential 8'][key].hasOwnProperty(actualKey)) {
                                    controls['Essential 8'][key][actualKey] = value2;
                                }
                            }
                        });
                        e8Level2.docs.forEach((doc) => {
                            const docData = doc.data();
                            const standard = filters.standards['Essential 8'];
                            const valuesToGrab = standard.valuesToGrab;
                            const valuesForResponse = standard.valuesForResponse;

                            if (!controls.hasOwnProperty('Essential 8')) {
                                controls['Essential 8'] = {};
                            }

                            let standardControls = docData;
                            let key = doc.id;
                            let value = standardControls;
                            if (!controls['Essential 8'].hasOwnProperty(key)) {
                                controls['Essential 8'][key] = {
                                    standard: 'Essential 8',
                                    framework: 'E8Controls',
                                    controlApplicable: true,
                                };
                            }

                            for (let j = 0; j < valuesToGrab.length; j++) {
                                let unprocessedKey = valuesToGrab[j].split('&');
                                let actualKey = unprocessedKey[0];
                                let key2 = unprocessedKey[1];
                                let valueKeys = key2.split('|');
                                let value2 = '';
                                if (value.hasOwnProperty(valueKeys[0])) {
                                    value2 = value[valueKeys[0]];
                                } else if (value.hasOwnProperty(valueKeys[1])) {
                                    value2 = value[valueKeys[1]];
                                }

                                if (!controls['Essential 8'][key].hasOwnProperty(actualKey)) {
                                    controls['Essential 8'][key][actualKey] = value2;
                                }
                            }
                        });
                        e8Level3.docs.forEach((doc) => {
                            const docData = doc.data();
                            const standard = filters.standards['Essential 8'];
                            const valuesToGrab = standard.valuesToGrab;
                            const valuesForResponse = standard.valuesForResponse;

                            if (!controls.hasOwnProperty('Essential 8')) {
                                controls['Essential 8'] = {};
                            }

                            let standardControls = docData;
                            let key = doc.id;
                            let value = standardControls;
                            if (!controls['Essential 8'].hasOwnProperty(key)) {
                                controls['Essential 8'][key] = {
                                    standard: 'Essential 8',
                                    framework: 'E8Controls',
                                    controlApplicable: true,
                                };
                            }

                            for (let j = 0; j < valuesToGrab.length; j++) {
                                let unprocessedKey = valuesToGrab[j].split('&');
                                let actualKey = unprocessedKey[0];
                                let key2 = unprocessedKey[1];
                                let valueKeys = key2.split('|');
                                let value2 = '';
                                if (value.hasOwnProperty(valueKeys[0])) {
                                    value2 = value[valueKeys[0]];
                                } else if (value.hasOwnProperty(valueKeys[1])) {
                                    value2 = value[valueKeys[1]];
                                }

                                if (!controls['Essential 8'][key].hasOwnProperty(actualKey)) {
                                    controls['Essential 8'][key][actualKey] = value2;
                                }
                            }
                        });
                    } else if (doc.id.toLowerCase().includes('ism')) {
                        if (filters.standards['ISM'] === undefined) return;
                        ismControls.docs.forEach((doc) => {
                            const docData = doc.data();
                            const standard = filters.standards['ISM'];
                            const valuesToGrab = standard.valuesToGrab;
                            const valuesForResponse = standard.valuesForResponse;

                            if (!controls.hasOwnProperty('ISM')) {
                                controls['ISM'] = {};
                            }

                            let standardControls = docData;
                            let key = doc.id;
                            let value = standardControls;
                            if (!controls['ISM'].hasOwnProperty(key)) {
                                controls['ISM'][key] = {
                                    standard: 'ISM',
                                    framework: 'ism',
                                    controlApplicable: true,
                                };
                            }

                            for (let j = 0; j < valuesToGrab.length; j++) {
                                let unprocessedKey = valuesToGrab[j].split('&');
                                let actualKey = unprocessedKey[0];
                                let key2 = unprocessedKey[1];
                                let valueKeys = key2.split('|');
                                let value2 = '';
                                if (value.hasOwnProperty(valueKeys[0])) {
                                    value2 = value[valueKeys[0]];
                                } else if (value.hasOwnProperty(valueKeys[1])) {
                                    value2 = value[valueKeys[1]];
                                }

                                if (!controls['ISM'][key].hasOwnProperty(actualKey)) {
                                    controls['ISM'][key][actualKey] = value2;
                                }
                            }
                        });
                    }
                }
            }
        });

        const getAssets = async () => {
            const assetValuesToGrab = filters.assets.valuesToGrab;
            const registerData = await AssetRegister.Asset.Get();
            const registerKeys = Object.keys(registerData);

            for (const assetKey of registerKeys) {
                const value = registerData[assetKey];

                if (!assets.hasOwnProperty(assetKey)) {
                    assets[assetKey] = {};
                }

                if ((value.hasOwnProperty('assetIDstatus') && value.assetIDstatus === 'deleted') ||
                    (!value.hasOwnProperty('assetIDstatus') &&
                        value.hasOwnProperty('assetIDStatus') && value.assetIDStatus === 'deleted')) {
                    continue;
                } else {
                    console.log("Asset is ", value.hasOwnProperty('assetIDstatus') && value.assetIDstatus ? value.assetIDstatus : (!value.hasOwnProperty('assetIDstatus') &&
                        value.hasOwnProperty('assetIDStatus') && value.assetIDStatus === 'deleted') ? value.assetIDStatus : '');
                }

                for (let j = 0; j < assetValuesToGrab.length; j++) {
                    let unprocessedKey = assetValuesToGrab[j].split('&');
                    let actualKey = unprocessedKey[0];
                    let key2 = unprocessedKey[1];
                    let valueKeys = key2.split('|');
                    let value2 = '';
                    if (value.hasOwnProperty(valueKeys[0])) {
                        value2 = value[valueKeys[0]];
                    } else if (value.hasOwnProperty(valueKeys[1])) {
                        value2 = value[valueKeys[1]];
                    }

                    if (actualKey === 'dateAdded' && value2 instanceof FirebaseTimestamp) {
                        if (!assets[assetKey].hasOwnProperty(actualKey)) {
                            assets[assetKey][actualKey] = value2.toDate();
                        }
                    } if (actualKey === 'dateAdded') {
                        if (!assets[assetKey].hasOwnProperty(actualKey)) {
                            assets[assetKey][actualKey] = new Date(value2);
                        }
                    } else if (!assets[assetKey]?.[actualKey]) {
                        assets[assetKey][actualKey] = value2;
                    }
                }

            }
        }

        const getRisks = async () => {
            const riskValuesToGrab = filters.risks.valuesToGrab;
            const registerData = await RiskRegister.Risk.Get();
            const registerKeys = Object.keys(registerData);

            for (const riskKey of registerKeys) {
                const value = registerData[riskKey];

                if (!risks.hasOwnProperty(riskKey)) {
                    risks[riskKey] = {};
                }

                if (value.hasOwnProperty("riskIDstatus") && value.riskIDstatus === "deleted") {
                    continue;
                } else { console.log("Risk is ", value.riskIDstatus); }

                for (let j = 0; j < riskValuesToGrab.length; j++) {
                    let unprocessedKey = riskValuesToGrab[j].split('&');
                    let actualKey = unprocessedKey[0];
                    let key2 = unprocessedKey[1];
                    let valueKeys = key2.split('|');
                    let value2 = '';
                    if (value.hasOwnProperty(valueKeys[0])) {
                        value2 = value[valueKeys[0]];
                    } else if (value.hasOwnProperty(valueKeys[1])) {
                        value2 = value[valueKeys[1]];
                    }

                    if (actualKey === 'dateAdded' && value2 instanceof FirebaseTimestamp) {
                        if (!risks[riskKey].hasOwnProperty(actualKey)) {
                            risks[riskKey][actualKey] = value2.toDate();
                        }
                    } if (actualKey === 'dateAdded') {
                        if (!risks[riskKey].hasOwnProperty(actualKey)) {
                            risks[riskKey][actualKey] = new Date(value2);
                        }
                    } else if (!risks[riskKey].hasOwnProperty(actualKey)) {
                        risks[riskKey][actualKey] = value2;
                    }
                }

            }

        }

        await getAssets();
        await getRisks();

        customerComplianceSnapshot.forEach((doc) => {
            const docData = doc.data();
            const assetValidDocuments = filters.assets.validDocuments;
            const assetValuesToGrab = filters.assets.valuesToGrab;
            const riskValidDocuments = filters.risks.validDocuments;
            const riskValuesToGrab = filters.risks.valuesToGrab;

            if (assetValidDocuments.includes(doc.id)) {
                // const registerData = docData.register.assets;
                // const registerKeys = Object.keys(registerData);

                // for (const assetKey of registerKeys) {
                //     const value = registerData[assetKey];

                //     if (!assets.hasOwnProperty(assetKey)) {
                //         assets[assetKey] = {};
                //     }

                //     if ((value.hasOwnProperty('assetIDstatus') && value.assetIDstatus === 'deleted') ||
                //         (!value.hasOwnProperty('assetIDstatus') &&
                //             value.hasOwnProperty('assetIDStatus') && value.assetIDStatus === 'deleted')) {
                //         continue;
                //     } else {
                //         console.log("Asset is ", value.hasOwnProperty('assetIDstatus') && value.assetIDstatus ? value.assetIDstatus : (!value.hasOwnProperty('assetIDstatus') &&
                //             value.hasOwnProperty('assetIDStatus') && value.assetIDStatus === 'deleted') ? value.assetIDStatus : '');
                //     }

                //     for (let j = 0; j < assetValuesToGrab.length; j++) {
                //         let unprocessedKey = assetValuesToGrab[j].split('&');
                //         let actualKey = unprocessedKey[0];
                //         let key2 = unprocessedKey[1];
                //         let valueKeys = key2.split('|');
                //         let value2 = '';
                //         if (value.hasOwnProperty(valueKeys[0])) {
                //             value2 = value[valueKeys[0]];
                //         } else if (value.hasOwnProperty(valueKeys[1])) {
                //             value2 = value[valueKeys[1]];
                //         }

                //         if (actualKey === 'dateAdded') {
                //             if (!assets[assetKey].hasOwnProperty(actualKey)) {
                //                 assets[assetKey][actualKey] = value2.toDate();
                //             }
                //         } else if (!assets[assetKey]?.[actualKey]) {
                //             assets[assetKey][actualKey] = value2;
                //         }
                //     }

                // }

            } else if (riskValidDocuments.includes(doc.id)) {
                // const registerData = docData.register.risks;
                // const registerKeys = Object.keys(registerData);

                // for (const riskKey of registerKeys) {
                //     const value = registerData[riskKey];

                //     if (!risks.hasOwnProperty(riskKey)) {
                //         risks[riskKey] = {};
                //     }

                //     if (value.hasOwnProperty("riskIDstatus") && value.riskIDstatus === "deleted") {
                //         continue;
                //     } else { console.log("Risk is ", value.riskIDstatus); }

                //     for (let j = 0; j < riskValuesToGrab.length; j++) {
                //         let unprocessedKey = riskValuesToGrab[j].split('&');
                //         let actualKey = unprocessedKey[0];
                //         let key2 = unprocessedKey[1];
                //         let valueKeys = key2.split('|');
                //         let value2 = '';
                //         if (value.hasOwnProperty(valueKeys[0])) {
                //             value2 = value[valueKeys[0]];
                //         } else if (value.hasOwnProperty(valueKeys[1])) {
                //             value2 = value[valueKeys[1]];
                //         }

                //         if (actualKey === 'dateAdded') {
                //             if (!risks[riskKey].hasOwnProperty(actualKey)) {
                //                 risks[riskKey][actualKey] = value2.toDate();
                //             }
                //         } else if (!risks[riskKey].hasOwnProperty(actualKey)) {
                //             risks[riskKey][actualKey] = value2;
                //         }
                //     }

                // }

            }

        })

        customerKeyDocsCollectionSnapshot.forEach((doc) => {
            const docData = doc.data();
            const ismsManualValidDocuments = filters.ismsManual.validDocuments;
            const ismsManualMapsToGrab = filters.ismsManual.mapsToGrab;

            if (ismsManualValidDocuments.includes(doc.id)) {
                const ismsManualKeys = Object.keys(docData);
                for (const mapID of ismsManualKeys) {
                    for (const mapToGrab of ismsManualMapsToGrab) {
                        console.log(mapToGrab)
                        console.log(mapID)
                        console.log(mapToGrab?.name, mapID)
                        if (mapToGrab?.name === mapID) {
                            const mapData = docData[mapID];
                            const mapKeys = Object.keys(mapData);
                            const ismsManualValuesToGrab = mapToGrab.valuesToGrab;

                            for (const key of mapKeys) {
                                const value = mapData[key];
                                console.log(value)

                                if (![mapID].hasOwnProperty(key)) {
                                    if (mapID === 'legalObligations') {
                                        legalObligations[key] = {};
                                    } else if (mapID === 'ismsObjectives') {
                                        ismsObjectives[key] = {};
                                    }
                                }

                                for (let j = 0; j < ismsManualValuesToGrab.length; j++) {
                                    let unprocessedKey = ismsManualValuesToGrab[j].split('&');
                                    let actualKey = unprocessedKey[0];
                                    let key2 = unprocessedKey[1];
                                    let valueKeys = key2.split('|');
                                    let value2 = '';

                                    if (value.hasOwnProperty(valueKeys[0])) {
                                        value2 = value[valueKeys[0]];
                                    } else if (value.hasOwnProperty(valueKeys[1])) {
                                        value2 = value[valueKeys[1]];
                                    }

                                    if (mapID === 'legalObligations') {
                                        if (!legalObligations[key]) continue; // Skip if key is not defined
                                        if (!legalObligations[key].hasOwnProperty('id')) {
                                            legalObligations[key]['id'] = key;
                                        }
                                        if (!legalObligations[key]?.[actualKey]) {
                                            legalObligations[key][actualKey] = value2;
                                        }
                                    } else if (mapID === 'ismsObjectives') {
                                        if (!ismsObjectives[key]) continue; // Skip if key is not defined
                                        if (!ismsObjectives[key].hasOwnProperty('id')) {
                                            ismsObjectives[key]['id'] = key;
                                        }
                                        if (!ismsObjectives[key]?.[actualKey]) {
                                            ismsObjectives[key][actualKey] = value2;
                                        }
                                    }
                                }

                            }

                        }
                    }
                }

            }

        })
        console.log('Controls:', controls);
        console.log('Assets:', assets);
        console.log('Risks:', risks);
        console.log('Legal Obligations:', legalObligations);
        console.log('ISMS Objectives:', ismsObjectives);

        window.controls = controls;
        window.assets = assets;
        window.risks = risks;
        window.legalObligations = legalObligations;
        window.ismsObjectives = ismsObjectives;

        return { controls, assets, risks };
    } catch (error) {
        console.error('Error fetching documents:', error);
    }
}

window.loadData = loadData;

function searchObject(obj, query, acceptedKeys = []) {
    let results = [];
    for (const key in obj) {
        if (typeof obj[key] === 'object') {
            if (acceptedKeys.length === 0 || acceptedKeys.includes(key)) {
                // Search this level if it's an accepted key
                if (typeof obj[key] === 'object') {
                    const nestedResults = searchObject(obj[key], query);
                    results = results.concat(nestedResults);
                }
            } else {
                // Skip this branch if not an accepted key
                continue;
            }
        } else if (
            typeof obj[key] === 'string' &&
            obj[key].toLowerCase().includes(query.toLowerCase())
        ) {
            results.push(obj);
            break; // Stop searching this branch since we already found a match
        }
    }
    return results;
}

window.searchNoPopulate = (query, acceptedKeys = [], mode = "controls", modalID = "#selectControlsModal") => {
    if (mode === "controls") {
        if (acceptedKeys < 1) {
            const activeButtons = document.querySelectorAll(
                `${modalID} .page-buttons .btn.active`,
            );

            for (let i = 0; i < activeButtons.length; i++) {
                acceptedKeys.push(activeButtons[i].innerText);
            }
        }

        const result = searchObject(window.controls, query, acceptedKeys);
        console.log(`Search Filters: `, acceptedKeys);
        console.log(`Searched controls: ${query}`);
        console.log(`Search result:`, result);
        return result;
    } else if (mode === "assets") {
        if (acceptedKeys < 1) {
            const activeButtons = document.querySelectorAll(
                `${modalID} .page-buttons .btn.active`,
            );

            for (let i = 0; i < activeButtons.length; i++) {
                acceptedKeys.push(activeButtons[i].innerText);
            }
        }

        const result = searchObject(window.assets, query, acceptedKeys);
        console.log(`Search Filters: `, acceptedKeys);
        console.log(`Searched assets: ${query}`);
        console.log(`Search result:`, result);
        return result;
    } else if (mode === "risks") {
        if (acceptedKeys < 1) {
            const activeButtons = document.querySelectorAll(
                `${modalID} .page-buttons .btn.active`,
            );

            for (let i = 0; i < activeButtons.length; i++) {
                acceptedKeys.push(activeButtons[i].innerText);
            }
        }

        const result = searchObject(window.risks, query, acceptedKeys);
        console.log(`Search Filters: `, acceptedKeys);
        console.log(`Searched risks: ${query}`);
        console.log(`Search result:`, result);
        return result;
    } else if (mode === "legalObligations") {
        if (acceptedKeys < 1) {
            const activeButtons = document.querySelectorAll(
                `${modalID} .page-buttons .btn.active`,
            );

            for (let i = 0; i < activeButtons.length; i++) {
                acceptedKeys.push(activeButtons[i].innerText);
            }
        }

        const result = searchObject(window.legalObligations, query, acceptedKeys);
        console.log(`Search Filters: `, acceptedKeys);
        console.log(`Searched risks: ${query}`);
        console.log(`Search result:`, result);
        return result;
    } else if (mode === "ismsObjectives") {
        if (acceptedKeys < 1) {
            const activeButtons = document.querySelectorAll(
                `${modalID} .page-buttons .btn.active`,
            );

            for (let i = 0; i < activeButtons.length; i++) {
                acceptedKeys.push(activeButtons[i].innerText);
            }
        }

        const result = searchObject(window.ismsObjectives, query, acceptedKeys);
        console.log(`Search Filters: `, acceptedKeys);
        console.log(`Searched risks: ${query}`);
        console.log(`Search result:`, result);
        return result;
    }
};

function resetButtons(makeActive = false, toMakeActive = null, modalID = "#selectControlsModal") {
    const buttons = document.querySelectorAll(`${modalID} .page-buttons .btn`);
    buttons.forEach((button) => {
        button.classList.remove('active');
        if (makeActive && toMakeActive !== null && button.innerText === toMakeActive) {
            button.classList.add('active');
        }
    });
}

window.listControls = async (filters = window.acceptedKeyFilters || [], modalID = "#selectControlsModal",) => {
    // Ensure filters is an array
    if (!Array.isArray(filters)) {
        filters = [];
    }

    if (!Array.isArray(filters) || filters.length === 0) {
        // Use the EnabledModulesManager to determine which control to show by default
        try {
            // Then check if ISO 27001 is enabled
            const isISO27001Enabled = await isStandardEnabled('ISO 27001');
            if (isISO27001Enabled) {
                filters.push('ISO 27001');
                // Set ISO 27001 button to active and all other to be inactive
                resetButtons(true, 'ISO 27001', modalID);
            } else {
                /// First check if Privacy Act is enabled
                const isPrivacyActEnabled = await isStandardEnabled('Privacy Act');
                if (isPrivacyActEnabled) {
                    filters.push('Privacy Act');
                    // Set Privacy Act button to active and all other to be inactive
                    resetButtons(true, 'Privacy Act', modalID);
                } else {
                    // If neither is enabled, get all enabled modules and use the first one
                    const enabledModules = await getEnabledModules();

                    // Check if we have any enabled modules in our standards list
                    for (const standardKey of Object.keys(filters.standards)) {
                        const moduleKey = standardKey.toLowerCase().replace(/\s+/g, '');
                        if (enabledModules.has(moduleKey)) {
                            filters.push(standardKey);
                            // Set the button to active
                            resetButtons(true, standardKey, modalID);
                            break; // Use the first one we find
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error determining default controls:', error);
            // Fall back to the original behavior based on customer plan
            filters.push(window.customerPlan?.toLowerCase() === 'starter' ? 'Privacy Act' : 'ISO 27001');
        }
    }

    // Only search when filters are explicitly provided
    const result = searchNoPopulate('', filters, "controls");
    console.log(`Search Filters: `, filters);
    console.log(`Searched controls: `, result);

    for (const control in result) {
        let controlData = result[control];
        let controlEl = window.controlElementTemplate.cloneNode(true);

        // if any key in controlData is empty, skip this control
        let hasEmptyKeys = false;
        for (const key in controlData) {
            if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                hasEmptyKeys = true;
                break;
            } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                hasEmptyKeys = true;
                break;
            } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                hasEmptyKeys = true;
                break;
            } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                hasEmptyKeys = true;
                break;
            }
        }

        // if (hasEmptyKeys) {
        //   continue;
        // }

        controlEl.removeAttribute('style');

        controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
        controlEl.querySelector('input[type="checkbox"]').id = controlData.controlID + '-checkbox';
        controlEl.querySelector('input[type="checkbox"]').checked = false;

        controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
            let controlData = JSON.parse(e.target.value);

            if (!e.target.checked) {
                // Remove this control from the list of selected controls
                let controlIndex = window.selectedControls.findIndex(
                    (control) => control.controlID === controlData.controlID,
                );

                if (controlIndex !== -1) {
                    console.log('Removing control:', controlData.controlID, 'from selected controls');
                    // Remove the control from the array
                    window.selectedControls.splice(controlIndex, 1);

                    // Update any other relevant data structures or UI elements here
                }
            } else {
                // Add this control to the list of selected controls
                console.log('Add control:', controlData.controlID, 'to selected controls');
                window.selectedControls.push(controlData);
            }
        });

        controlEl
            .querySelector('input[type="checkbox"]')
            .parentElement.setAttribute('for', controlData.controlID + '-checkbox');

        controlEl.querySelector('.meta-standard').textContent = controlData.standard;
        controlEl.querySelector('.meta-id').textContent = controlData.standard === 'SMB1001' ?
            `${controlData.controlID} (${controlData.level})` : controlData.controlID;
        controlEl.querySelector('.control-name').textContent = controlData.controlName.replaceAll(
            'Acme Inc',
            window.companyName,
        );
        controlEl.querySelector('.control-description').textContent =
            (controlData?.controlDescription ?? controlData?.implementationGuidance).replaceAll('Acme Inc', window.companyName);

        document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
    }

    return result;
};

window.listAssets = (filters = window.acceptedKeyFilters || [], modalID = "#selectControlsModal",) => {
    filters = [];
    const result = searchNoPopulate('', filters, "assets");
    console.log(`Search Filters: `, filters);
    console.log(`Searched controls: `, result);

    for (const control in result) {
        let controlData = result[control];
        let controlEl = window.controlElementTemplate.cloneNode(true);

        // if any key in controlData is empty, skip this control
        let hasEmptyKeys = false;
        for (const key in controlData) {
            if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                hasEmptyKeys = true;
                break;
            } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                hasEmptyKeys = true;
                break;
            } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                hasEmptyKeys = true;
                break;
            } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                hasEmptyKeys = true;
                break;
            }
        }

        // if (hasEmptyKeys) {
        //   continue;
        // }

        controlEl.removeAttribute('style');

        controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
        controlEl.querySelector('input[type="checkbox"]').id = controlData.assetID + '-checkbox';
        controlEl.querySelector('input[type="checkbox"]').checked = false;

        controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
            let controlData = JSON.parse(e.target.value);

            if (!e.target.checked) {
                // Remove this control from the list of selected controls
                let controlIndex = window.selectedAssets.findIndex(
                    (control) => control.assetID === controlData.assetID,
                );

                if (controlIndex !== -1) {
                    console.log('Removing control:', controlData.assetID, 'from selected controls');
                    // Remove the control from the array
                    window.selectedAssets.splice(controlIndex, 1);

                    // Update any other relevant data structures or UI elements here
                }
            } else {
                // Add this control to the list of selected controls
                console.log('Add control:', controlData.assetID, 'to selected controls');
                window.selectedAssets.push(controlData);
            }
        });

        controlEl
            .querySelector('input[type="checkbox"]')
            .parentElement.setAttribute('for', controlData.assetID + '-checkbox');

        controlEl.querySelector('.meta-standard').textContent = "Asset";
        controlEl.querySelector('.meta-id').textContent = controlData.assetID;
        controlEl.querySelector('.control-name').textContent = controlData.assetDesc.replaceAll(
            'Acme Inc',
            window.companyName,
        );
        controlEl.querySelector('.control-description').textContent = "Date added: " + new Date(controlData.dateAdded).toDateString();

        document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
    }

    return result;
};

window.listRisks = (filters = window.acceptedKeyFilters || [], modalID = "#selectControlsModal",) => {
    filters = [];
    const result = searchNoPopulate('', filters, "risks");
    console.log(`Search Filters: `, filters);
    console.log(`Searched controls: `, result);

    for (const control in result) {
        let controlData = result[control];
        let controlEl = window.controlElementTemplate.cloneNode(true);
        if (controlData?.riskIDstatus !== undefined && controlData?.riskIDstatus.toLowerCase() === 'deleted') continue;

        // if any key in controlData is empty, skip this control
        let hasEmptyKeys = false;
        for (const key in controlData) {
            if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                hasEmptyKeys = true;
                break;
            } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                hasEmptyKeys = true;
                break;
            } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                hasEmptyKeys = true;
                break;
            } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                hasEmptyKeys = true;
                break;
            }
        }

        // if (hasEmptyKeys) {
        //   continue;
        // }

        controlEl.removeAttribute('style');

        controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
        controlEl.querySelector('input[type="checkbox"]').id = controlData.riskID + '-checkbox';
        controlEl.querySelector('input[type="checkbox"]').checked = false;

        controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
            let controlData = JSON.parse(e.target.value);

            if (!e.target.checked) {
                // Remove this control from the list of selected controls
                let controlIndex = window.selectedRisks.findIndex(
                    (control) => control.riskID === controlData.riskID,
                );

                if (controlIndex !== -1) {
                    console.log('Removing control:', controlData.riskID, 'from selected controls');
                    // Remove the control from the array
                    window.selectedRisks.splice(controlIndex, 1);

                    // Update any other relevant data structures or UI elements here
                }
            } else {
                // Add this control to the list of selected controls
                console.log('Add control:', controlData.riskID, 'to selected controls');
                window.selectedRisks.push(controlData);
            }
        });

        controlEl
            .querySelector('input[type="checkbox"]')
            .parentElement.setAttribute('for', controlData.riskID + '-checkbox');

        controlEl.querySelector('.meta-standard').textContent = "Risk";
        controlEl.querySelector('.meta-id').textContent = controlData.riskID;
        controlEl.querySelector('.control-name').textContent = controlData.risk.replaceAll(
            'Acme Inc',
            window.companyName,
        );
        controlEl.querySelector('.control-description').textContent = "Date added: " + new Date(controlData.dateAdded).toDateString();

        document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
    }

    return result;
};

window.listLegalObligations = (filters = window.acceptedKeyFilters || [], modalID = "#selectControlsModal",) => {
    filters = [];
    const result = searchNoPopulate('', filters, "legalObligations");
    console.log(`Search Filters: `, filters);
    console.log(`Searched controls: `, result);

    for (const control in result) {
        let controlData = result[control];
        let controlEl = window.controlElementTemplate.cloneNode(true);

        // if any key in controlData is empty, skip this control
        let hasEmptyKeys = false;
        for (const key in controlData) {
            if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                hasEmptyKeys = true;
                break;
            } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                hasEmptyKeys = true;
                break;
            } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                hasEmptyKeys = true;
                break;
            } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                hasEmptyKeys = true;
                break;
            }
        }

        // if (hasEmptyKeys) {
        //   continue;
        // }

        controlEl.removeAttribute('style');

        controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
        controlEl.querySelector('input[type="checkbox"]').id = controlData.id + '-checkbox';
        controlEl.querySelector('input[type="checkbox"]').checked = false;

        controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
            let controlData = JSON.parse(e.target.value);

            if (!e.target.checked) {
                // Remove this control from the list of selected controls
                let controlIndex = window.selectedLegalObligations.findIndex(
                    (control) => control.id === controlData.id,
                );

                if (controlIndex !== -1) {
                    console.log('Removing control:', controlData.id, 'from selected controls');
                    // Remove the control from the array
                    window.selectedLegalObligations.splice(controlIndex, 1);

                    // Update any other relevant data structures or UI elements here
                }
            } else {
                // Add this control to the list of selected controls
                console.log('Add control:', controlData.id, 'to selected controls');
                window.selectedLegalObligations.push(controlData);
            }
        });

        controlEl
            .querySelector('input[type="checkbox"]')
            .parentElement.setAttribute('for', controlData.id + '-checkbox');

        controlEl.querySelector('.meta-standard').textContent = "Legal Obligation";
        controlEl.querySelector('.meta-id').textContent = controlData.id;
        controlEl.querySelector('.control-name').textContent = controlData.requirement.replaceAll(
            'Acme Inc',
            window.companyName,
        );
        controlEl.querySelector('.control-description').textContent = "Date added: " + new Date(controlData.dateAdded).toDateString();

        document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
    }

    return result;
};

window.listISMSObjectives = (filters = window.acceptedKeyFilters || [], modalID = "#selectControlsModal",) => {
    filters = [];
    const result = searchNoPopulate('', filters, "ismsObjectives");
    console.log(`Search Filters: `, filters);
    console.log(`Searched controls: `, result);

    for (const control in result) {
        let controlData = result[control];
        let controlEl = window.controlElementTemplate.cloneNode(true);

        // if any key in controlData is empty, skip this control
        let hasEmptyKeys = false;
        for (const key in controlData) {
            if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                hasEmptyKeys = true;
                break;
            } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                hasEmptyKeys = true;
                break;
            } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                hasEmptyKeys = true;
                break;
            } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                hasEmptyKeys = true;
                break;
            }
        }

        // if (hasEmptyKeys) {
        //   continue;
        // }

        controlEl.removeAttribute('style');

        controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
        controlEl.querySelector('input[type="checkbox"]').id = controlData.id + '-checkbox';
        controlEl.querySelector('input[type="checkbox"]').checked = false;

        controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
            let controlData = JSON.parse(e.target.value);

            if (!e.target.checked) {
                // Remove this control from the list of selected controls
                let controlIndex = window.selectedISMSObjectives.findIndex(
                    (control) => control.id === controlData.id,
                );

                if (controlIndex !== -1) {
                    console.log('Removing control:', controlData.id, 'from selected controls');
                    // Remove the control from the array
                    window.selectedISMSObjectives.splice(controlIndex, 1);

                    // Update any other relevant data structures or UI elements here
                }
            } else {
                // Add this control to the list of selected controls
                console.log('Add control:', controlData.id, 'to selected controls');
                window.selectedISMSObjectives.push(controlData);
            }
        });

        controlEl
            .querySelector('input[type="checkbox"]')
            .parentElement.setAttribute('for', controlData.id + '-checkbox');

        controlEl.querySelector('.meta-standard').textContent = "ISMS Objective";
        controlEl.querySelector('.meta-id').textContent = controlData.id;
        controlEl.querySelector('.control-name').textContent = controlData.objectiveText.replaceAll(
            'Acme Inc',
            window.companyName,
        );
        controlEl.querySelector('.control-description').textContent = "Target date: " + new Date(controlData.objectiveTargetDate).toDateString();

        document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
    }

    return result;
};

window.findControlsByIds = (controlIds, filters = window.acceptedKeyFilters || []) => {
    let controls = [];
    let globalControls = { ...window.controls };
    for (const controlId of controlIds) {
        if (filters.length > 0) {
            // Loop through globalControls and grab the keyname and then check if the key name is in the filters array and delete it if not
            for (let i = 0; i < Object.keys(globalControls).length; i++) {
                let key = Object.keys(globalControls)[i];
                let value = globalControls[key];
                if (!filters.includes(key)) {
                    console.log('Deleting key:', key);
                    delete globalControls[key];
                }
            }
        }

        console.log(
            'Checking ',
            controlId,
            'against filters:',
            filters,
            'globalControls:',
            globalControls,
        );
        const result = searchControlById(globalControls, controlId);
        if (result) {
            controls.push(result);
        }
    }

    return controls;
};

window.findAssetsByIds = (controlIds, filters = window.acceptedKeyFilters || []) => {
    let controls = [];
    let globalControls = { ...window.assets };
    for (const controlId of controlIds) {
        console.log(controlId)
        if (filters.length > 0) {
            // Loop through globalControls and grab the keyname and then check if the key name is in the filters array and delete it if not
            for (let i = 0; i < Object.keys(globalControls).length; i++) {
                let key = Object.keys(globalControls)[i];
                let value = globalControls[key];
                if (!filters.includes(key)) {
                    console.log('Deleting key:', key);
                    delete globalControls[key];
                }
            }
        }

        console.log(
            'Checking ',
            controlId,
            'against filters:',
            filters,
            'globalControls:',
            globalControls,
        );
        const result = searchForAssetByID(globalControls, controlId);
        if (result) {
            controls.push(result);
        }
    }

    return controls;
};

window.findRisksByIds = (controlIds, filters = window.acceptedKeyFilters || []) => {
    let controls = [];
    let globalControls = { ...window.risks };
    for (const controlId of controlIds) {
        console.log(
            'Checking ',
            controlId,
            'against filters:',
            filters,
            'globalControls:',
            globalControls,
        );
        const result = searchForRiskByID(globalControls, controlId);
        if (result) {
            controls.push(result);
        }
    }

    return controls;
};

window.findLegalObligationsByIds = (controlIds, filters = window.acceptedKeyFilters || []) => {
    let controls = [];
    let globalControls = { ...window.legalObligations };
    for (const controlId of controlIds) {
        if (filters.length > 0) {
            // Loop through globalControls and grab the keyname and then check if the key name is in the filters array and delete it if not
            for (let i = 0; i < Object.keys(globalControls).length; i++) {
                let key = Object.keys(globalControls)[i];
                let value = globalControls[key];
                if (!filters.includes(key)) {
                    console.log('Deleting key:', key);
                    delete globalControls[key];
                }
            }
        }

        console.log(
            'Checking ',
            controlId,
            'against filters:',
            filters,
            'globalControls:',
            globalControls,
        );
        const result = searchForLegalObligationByID(globalControls, controlId);
        if (result) {
            controls.push(result);
        }
    }

    return controls;
};

window.findISMSObjectivesByIds = (controlIds, filters = window.acceptedKeyFilters || []) => {
    let controls = [];
    let globalControls = { ...window.ismsObjectives };
    for (const controlId of controlIds) {
        if (filters.length > 0) {
            // Loop through globalControls and grab the keyname and then check if the key name is in the filters array and delete it if not
            for (let i = 0; i < Object.keys(globalControls).length; i++) {
                let key = Object.keys(globalControls)[i];
                let value = globalControls[key];
                if (!filters.includes(key)) {
                    console.log('Deleting key:', key);
                    delete globalControls[key];
                }
            }
        }

        console.log(
            'Checking ',
            controlId,
            'against filters:',
            filters,
            'globalControls:',
            globalControls,
        );
        const result = searchForISMSObjectiveByID(globalControls, controlId);
        if (result) {
            controls.push(result);
        }
    }

    return controls;
};

function searchControlById(obj, controlId) {
    for (const key in obj) {
        if (typeof obj[key] === 'object') {
            const result = searchControlById(obj[key], controlId);
            if (result) {
                return result;
            }
        } else if (obj['controlID'] === controlId) {
            return obj;
        }
    }
    return null;
}

function searchForAssetByID(obj, controlId) {
    for (const key in obj) {
        if (typeof obj[key] === 'object') {
            const result = searchForAssetByID(obj[key], controlId);
            if (result) {
                return result;
            }
        } else if (obj['assetID'] === parseInt(controlId)) {
            return obj;
        }
    }
    return null;
}

function searchForRiskByID(obj, controlId) {
    for (const key in obj) {
        if (typeof obj[key] === 'object') {
            const result = searchForRiskByID(obj[key], controlId);
            if (result) {
                return result;
            }
        } else if (obj['riskIDstatus'] !== "deleted" && obj['riskID'] === parseInt(controlId)) {
            return obj;
        }
    }
    return null;
}

function searchForLegalObligationByID(obj, controlId) {
    for (const key in obj) {
        if (typeof obj[key] === 'object') {
            const result = searchForLegalObligationByID(obj[key], controlId);
            if (result) {
                return result;
            }
        } else if (obj['id'] === controlId) {
            return obj;
        }
    }
    return null;
}

function searchForISMSObjectiveByID(obj, controlId) {
    for (const key in obj) {
        if (typeof obj[key] === 'object') {
            const result = searchForISMSObjectiveByID(obj[key], controlId);
            if (result) {
                return result;
            }
        } else if (obj['id'] === controlId) {
            return obj;
        }
    }
    return null;
}

function removeDuplicateControls() {
    var uniqueControls = {}; // Object to store unique identifiers
    var controlList = document.querySelector('.controls-list');

    // Gather all the control divs within the control list
    var controls = controlList.querySelectorAll('.control');

    // Iterate through each control div
    controls.forEach(function (control) {
        var checkboxValue = control.querySelector('input[type="checkbox"]').value;
        var controlName = control.querySelector('.control-name').innerText;
        var metaId = control.querySelector('.meta-id').innerText;
        var controlDescription = control.querySelector('.control-description').innerText;

        // Create a unique identifier for the control div
        var uniqueIdentifier = checkboxValue + controlName + metaId + controlDescription;

        // Check if the unique identifier already exists in the object
        if (!uniqueControls[uniqueIdentifier]) {
            // If it doesn't exist, add it to the object and keep the control div
            uniqueControls[uniqueIdentifier] = true;
        } else {
            // If it exists, remove the duplicate control div
            control.remove();
        }
    });
}

window.populateExistingControls = (storeResults = false, modalID = "#selectControlsModal", mode = "controls") => {
    if (mode === "controls") {
        let globalControls = window.selectedControls || [];
        const controlsToSearch = globalControls.map((item) => {
            if (typeof item === 'string') {
                return item;
            } else {
                return item.controlID;
            }
        });

        var result = findControlsByIds(controlsToSearch);
        console.log(`Searched controls:`, controlsToSearch);
        console.log(`Searched controls:`, controlsToSearch);
        console.log(`Search result:`, result);

        if (storeResults == true) {
            window.selectedControls = result;
        }

        document.querySelector(`${modalID} #controls-list`).innerHTML = '';

        if (result.length < 1) {
            return window.listControls(window.acceptedKeyFilters);
        }

        for (const control in result) {
            let controlData = result[control];
            let controlEl = window.controlElementTemplate.cloneNode(true);

            // if any key in controlData is empty, skip this control
            let hasEmptyKeys = false;
            for (const key in controlData) {
                if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                    hasEmptyKeys = true;
                    break;
                } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                }
            }

            // if (hasEmptyKeys) {
            //   continue;
            // }

            controlEl.removeAttribute('style');

            controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
            controlEl.querySelector('input[type="checkbox"]').id = controlData.controlID + '-checkbox';
            controlEl.querySelector('input[type="checkbox"]').checked = true;

            controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
                let controlData = JSON.parse(e.target.value);

                if (!e.target.checked) {
                    // Remove this control from the list of selected controls
                    let controlIndex = window.selectedControls.findIndex(
                        (control) => control.controlID === controlData.controlID,
                    );

                    if (controlIndex !== -1) {
                        console.log('Removing control:', controlData.controlID, 'from selected controls');
                        // Remove the control from the array
                        window.selectedControls.splice(controlIndex, 1);

                        // Update any other relevant data structures or UI elements here
                    }
                } else {
                    // Add this control to the list of selected controls
                    console.log('Add control:', controlData.controlID, 'to selected controls');
                    window.selectedControls.push(controlData.controlID);
                }
            });

            controlEl
                .querySelector('input[type="checkbox"]')
                .parentElement.setAttribute('for', controlData.controlID + '-checkbox');

            controlEl.querySelector('.meta-standard').textContent = controlData.standard;
            controlEl.querySelector('.meta-id').textContent = controlData.controlID;
            controlEl.querySelector('.control-name').textContent = controlData.controlName.replaceAll(
                'Acme Inc',
                window.companyName,
            );
            controlEl.querySelector('.control-description').textContent =
                (controlData?.controlDescription ?? controlData?.implementationGuidance).replaceAll('Acme Inc', window.companyName);

            document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
        }

        // Populate the rest of the list if window.acceptedKeyFilters is not empty
        if (window.acceptedKeyFilters.length > 0 || result.length >= 1) {
            return window.listControls(window.acceptedKeyFilters);
        }

        removeDuplicateControls();

        return result;
    } else if (mode === "assets") {
        let globalControls = window.selectedAssets || [];
        const controlsToSearch = globalControls.map((item) => {
            if (typeof item === 'string') {
                return item;
            } else {
                return item.assetID;
            }
        });

        var result = findAssetsByIds(controlsToSearch);
        console.log(`Searched assets:`, controlsToSearch);
        console.log(`Searched assets:`, controlsToSearch);
        console.log(`Search result:`, result);

        if (storeResults == true) {
            window.selectedAssets = result;
        }

        document.querySelector(`${modalID} #controls-list`).innerHTML = '';

        if (result.length < 1) {
            return window.listAssets(window.acceptedKeyFilters);
        }

        for (const control in result) {
            let controlData = result[control];
            let controlEl = window.controlElementTemplate.cloneNode(true);

            // if any key in controlData is empty, skip this control
            let hasEmptyKeys = false;
            for (const key in controlData) {
                if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                    hasEmptyKeys = true;
                    break;
                } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                }
            }

            // if (hasEmptyKeys) {
            //   continue;
            // }

            controlEl.removeAttribute('style');

            controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
            controlEl.querySelector('input[type="checkbox"]').id = controlData.assetID + '-checkbox';
            controlEl.querySelector('input[type="checkbox"]').checked = true;

            controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
                let controlData = JSON.parse(e.target.value);

                if (!e.target.checked) {
                    // Remove this control from the list of selected controls
                    let controlIndex = window.selectedAssets.findIndex(
                        (control) => control.assetID === controlData.assetID,
                    );

                    if (controlIndex !== -1) {
                        console.log('Removing control:', controlData.assetID, 'from selected controls');
                        // Remove the control from the array
                        window.selectedAssets.splice(controlIndex, 1);

                        // Update any other relevant data structures or UI elements here
                    }
                } else {
                    // Add this control to the list of selected controls
                    console.log('Add control:', controlData.assetID, 'to selected controls');
                    window.selectedAssets.push(controlData.assetID);
                }
            });

            controlEl
                .querySelector('input[type="checkbox"]')
                .parentElement.setAttribute('for', controlData.assetID + '-checkbox');

            controlEl.querySelector('.meta-standard').textContent = "Asset";
            controlEl.querySelector('.meta-id').textContent = controlData.assetID;
            controlEl.querySelector('.control-name').textContent = controlData.assetDesc.replaceAll(
                'Acme Inc',
                window.companyName,
            );
            controlEl.querySelector('.control-description').textContent = "Date added: " + new Date(controlData.dateAdded).toDateString();


            document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
        }

        // Populate the rest of the list if window.acceptedKeyFilters is not empty
        if (window.acceptedKeyFilters.length > 0 || result.length >= 1) {
            return window.listAssets(window.acceptedKeyFilters);
        }

        removeDuplicateControls();

        return result;
    } else if (mode === "risks") {
        let globalControls = window.selectedRisks || [];
        const controlsToSearch = globalControls.map((item) => {
            if (typeof item === 'string') {
                return item;
            } else {
                return item.riskID;
            }
        });

        var result = findRisksByIds(controlsToSearch);
        console.log(`Searched risks:`, controlsToSearch);
        console.log(`Searched risks:`, controlsToSearch);
        console.log(`Search result:`, result);

        if (storeResults == true) {
            window.selectedRisks = result;
        }

        document.querySelector(`${modalID} #controls-list`).innerHTML = '';

        if (result.length < 1) {
            return window.listRisks(window.acceptedKeyFilters);
        }

        for (const control in result) {
            let controlData = result[control];
            let controlEl = window.controlElementTemplate.cloneNode(true);

            // if any key in controlData is empty, skip this control
            let hasEmptyKeys = false;
            for (const key in controlData) {
                if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                    hasEmptyKeys = true;
                    break;
                } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                }
            }

            // if (hasEmptyKeys) {
            //   continue;
            // }

            controlEl.removeAttribute('style');

            controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
            controlEl.querySelector('input[type="checkbox"]').id = controlData.riskID + '-checkbox';
            controlEl.querySelector('input[type="checkbox"]').checked = true;

            controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
                let controlData = JSON.parse(e.target.value);

                if (!e.target.checked) {
                    // Remove this control from the list of selected controls
                    let controlIndex = window.selectedRisks.findIndex(
                        (control) => control.riskID === controlData.riskID,
                    );

                    if (controlIndex !== -1) {
                        console.log('Removing control:', controlData.riskID, 'from selected controls');
                        // Remove the control from the array
                        window.selectedRisks.splice(controlIndex, 1);

                        // Update any other relevant data structures or UI elements here
                    }
                } else {
                    // Add this control to the list of selected controls
                    console.log('Add control:', controlData.riskID, 'to selected controls');
                    window.selectedRisks.push(controlData.riskID);
                }
            });

            controlEl
                .querySelector('input[type="checkbox"]')
                .parentElement.setAttribute('for', controlData.riskID + '-checkbox');

            controlEl.querySelector('.meta-standard').textContent = "Risk";
            controlEl.querySelector('.meta-id').textContent = controlData.riskID;
            controlEl.querySelector('.control-name').textContent = controlData.risk.replaceAll(
                'Acme Inc',
                window.companyName,
            );
            controlEl.querySelector('.control-description').textContent = "Date added: " + new Date(controlData.dateAdded).toDateString();


            document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
        }

        // Populate the rest of the list if window.acceptedKeyFilters is not empty
        if (window.acceptedKeyFilters.length > 0 || result.length >= 1) {
            return window.listRisks(window.acceptedKeyFilters);
        }

        removeDuplicateControls();

        return result;
    } else if (mode === "legalObligations") {
        let globalControls = window.selectedLegalObligations || [];
        const controlsToSearch = globalControls.map((item) => {
            if (typeof item === 'string') {
                return item;
            } else {
                return item.id;
            }
        });

        var result = findLegalObligationsByIds(controlsToSearch);
        console.log(`Searched legal obligations:`, controlsToSearch);
        console.log(`Searched legal obligations:`, controlsToSearch);
        console.log(`Search result:`, result);

        if (storeResults == true) {
            window.selectedLegalObligations = result;
        }

        document.querySelector(`${modalID} #controls-list`).innerHTML = '';

        if (result.length < 1) {
            return window.listLegalObligations(window.acceptedKeyFilters);
        }

        for (const control in result) {
            let controlData = result[control];
            let controlEl = window.controlElementTemplate.cloneNode(true);

            // if any key in controlData is empty, skip this control
            let hasEmptyKeys = false;
            for (const key in controlData) {
                if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                    hasEmptyKeys = true;
                    break;
                } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                }
            }

            // if (hasEmptyKeys) {
            //   continue;
            // }

            controlEl.removeAttribute('style');

            controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
            controlEl.querySelector('input[type="checkbox"]').id = controlData.id + '-checkbox';
            controlEl.querySelector('input[type="checkbox"]').checked = true;

            controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
                let controlData = JSON.parse(e.target.value);

                if (!e.target.checked) {
                    // Remove this control from the list of selected controls
                    let controlIndex = window.selectedLegalObligations.findIndex(
                        (control) => control.id === controlData.id,
                    );

                    if (controlIndex !== -1) {
                        console.log('Removing control:', controlData.id, 'from selected controls');
                        // Remove the control from the array
                        window.selectedLegalObligations.splice(controlIndex, 1);

                        // Update any other relevant data structures or UI elements here
                    }
                } else {
                    // Add this control to the list of selected controls
                    console.log('Add control:', controlData.id, 'to selected controls');
                    window.selectedLegalObligations.push(controlData.id);
                }
            });

            controlEl
                .querySelector('input[type="checkbox"]')
                .parentElement.setAttribute('for', controlData.id + '-checkbox');

            controlEl.querySelector('.meta-standard').textContent = "Legal Obligation";
            controlEl.querySelector('.meta-id').textContent = controlData.id;
            controlEl.querySelector('.control-name').textContent = controlData.requirement.replaceAll(
                'Acme Inc',
                window.companyName,
            );
            controlEl.querySelector('.control-description').textContent = "Date added: " + new Date(controlData.dateAdded).toDateString();


            document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
        }

        // Populate the rest of the list if window.acceptedKeyFilters is not empty
        if (window.acceptedKeyFilters.length > 0 || result.length >= 1) {
            return window.listLegalObligations(window.acceptedKeyFilters);
        }

        removeDuplicateControls();

        return result;
    } else if (mode === "ismsObjectives") {
        let globalControls = window.selectedISMSObjectives || [];
        const controlsToSearch = globalControls.map((item) => {
            if (typeof item === 'string') {
                return item;
            } else {
                return item.id;
            }
        });

        var result = findISMSObjectivesByIds(controlsToSearch);
        console.log(`Searched ISMS objectives:`, controlsToSearch);
        console.log(`Searched ISMS objectives:`, controlsToSearch);
        console.log(`Search result:`, result);

        if (storeResults == true) {
            window.selectedISMSObjectives = result;
        }

        document.querySelector(`${modalID} #controls-list`).innerHTML = '';

        if (result.length < 1) {
            return window.listISMSObjectives(window.acceptedKeyFilters);
        }

        for (const control in result) {
            let controlData = result[control];
            let controlEl = window.controlElementTemplate.cloneNode(true);

            // if any key in controlData is empty, skip this control
            let hasEmptyKeys = false;
            for (const key in controlData) {
                if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                    hasEmptyKeys = true;
                    break;
                } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                }
            }

            // if (hasEmptyKeys) {
            //   continue;
            // }

            controlEl.removeAttribute('style');

            controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
            controlEl.querySelector('input[type="checkbox"]').id = controlData.id + '-checkbox';
            controlEl.querySelector('input[type="checkbox"]').checked = true;

            controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
                let controlData = JSON.parse(e.target.value);

                if (!e.target.checked) {
                    // Remove this control from the list of selected controls
                    let controlIndex = window.selectedISMSObjectives.findIndex(
                        (control) => control.id === controlData.id,
                    );

                    if (controlIndex !== -1) {
                        console.log('Removing control:', controlData.id, 'from selected controls');
                        // Remove the control from the array
                        window.selectedISMSObjectives.splice(controlIndex, 1);

                        // Update any other relevant data structures or UI elements here
                    }
                } else {
                    // Add this control to the list of selected controls
                    console.log('Add control:', controlData.id, 'to selected controls');
                    window.selectedISMSObjectives.push(controlData.id);
                }
            });

            controlEl
                .querySelector('input[type="checkbox"]')
                .parentElement.setAttribute('for', controlData.id + '-checkbox');

            controlEl.querySelector('.meta-standard').textContent = "ISMS Objective";
            controlEl.querySelector('.meta-id').textContent = controlData.id;
            controlEl.querySelector('.control-name').textContent = controlData.objectiveText.replaceAll(
                'Acme Inc',
                window.companyName,
            );
            controlEl.querySelector('.control-description').textContent = "Target date: " + new Date(controlData.objectiveTargetDate).toDateString();


            document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
        }

        // Populate the rest of the list if window.acceptedKeyFilters is not empty
        if (window.acceptedKeyFilters.length > 0 || result.length >= 1) {
            return window.listISMSObjectives(window.acceptedKeyFilters);
        }

        removeDuplicateControls();

        return result;
    }
};

window.searchAndPopulate = (query, acceptedKeys = window.acceptedKeyFilters || [], modalID = "#selectControlsModal", mode = "controls") => {
    if (mode === "controls") {
        if (acceptedKeys < 1) {
            const activeButtons = document.querySelectorAll(
                `${modalID} .page-buttons .btn.active`,
            );

            for (let i = 0; i < activeButtons.length; i++) {
                acceptedKeys.push(activeButtons[i].innerText);
            }
        }

        if (query.length < 1 || query === '' || query === ' ') {
            console.log('Nothing to search, populating existing controls');
            window.populateExistingControls(true, "#selectControlsModal", "controls");
            removeDuplicateControls();
            return;
        }

        console.log('Continued');

        const result = searchObject(window.controls, query, acceptedKeys);
        console.log(`Search Filters: `, acceptedKeys);
        console.log(`Searched controls: ${query}`);
        console.log(`Search result:`, result);

        document.querySelector(`${modalID} #controls-list`).innerHTML = '';

        for (const control in result) {
            let controlData = result[control];
            let controlEl = window.controlElementTemplate.cloneNode(true);

            // if any key in controlData is empty, skip this control
            let hasEmptyKeys = false;
            for (const key in controlData) {
                if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                    hasEmptyKeys = true;
                    break;
                } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                }
            }

            // if (hasEmptyKeys) {
            //   continue;
            // }

            controlEl.removeAttribute('style');

            controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
            controlEl.querySelector('input[type="checkbox"]').id = controlData.controlID + '-checkbox';

            let isSelected = window.selectedControls.some((selectedControl) => {
                return (
                    selectedControl.controlID === controlData.controlID &&
                    selectedControl.framework === controlData.framework
                );
            });

            // If so, make checkbox as checked
            if (isSelected) {
                let checkbox = controlEl.querySelector('input[type="checkbox"]');
                checkbox.checked = true;
            }

            controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
                let controlData = JSON.parse(e.target.value);

                if (!e.target.checked) {
                    // Remove this control from the list of selected controls
                    let controlIndex = window.selectedControls.findIndex(
                        (control) => control.controlID === controlData.controlID,
                    );

                    if (controlIndex !== -1) {
                        console.log('Removing control:', controlData.controlID, 'from selected controls');
                        // Remove the control from the array
                        window.selectedControls.splice(controlIndex, 1);

                        // Update any other relevant data structures or UI elements here
                    }
                } else {
                    // Add this control to the list of selected controls
                    console.log('Add control:', controlData.controlID, 'to selected controls');
                    window.selectedControls.push(controlData);
                }
            });

            controlEl
                .querySelector('input[type="checkbox"]')
                .parentElement.setAttribute('for', controlData.controlID + '-checkbox');

            controlEl.querySelector('.meta-standard').textContent = controlData.standard;
            controlEl.querySelector('.meta-id').textContent = controlData.standard === 'SMB1001' ?
                `${controlData.controlID} (${controlData.level})` : controlData.controlID;
            controlEl.querySelector('.control-name').textContent = controlData.controlName.replaceAll(
                'Acme Inc',
                window.companyName,
            );
            controlEl.querySelector('.control-description').textContent =
                (controlData?.controlDescription ?? controlData?.implementationGuidance).replaceAll('Acme Inc', window.companyName);

            document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
        }

        removeDuplicateControls();
    } else if (mode === "assets") {
        if (query.length < 1 || query === '' || query === ' ') {
            console.log('Nothing to search, populating existing controls');
            window.populateExistingControls(true, "#selectControlsModal", "assets");
            removeDuplicateControls();
            return;
        }

        console.log('Continued');

        const result = searchObject(window.assets, query, acceptedKeys);
        console.log(`Search Filters: `, acceptedKeys);
        console.log(`Searched assets: ${query}`);
        console.log(`Search result:`, result);

        document.querySelector(`${modalID} #controls-list`).innerHTML = '';

        for (const control in result) {
            let controlData = result[control];
            let controlEl = window.controlElementTemplate.cloneNode(true);

            // if any key in controlData is empty, skip this control
            let hasEmptyKeys = false;
            for (const key in controlData) {
                if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                    hasEmptyKeys = true;
                    break;
                } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                }
            }

            // if (hasEmptyKeys) {
            //   continue;
            // }

            controlEl.removeAttribute('style');

            controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
            controlEl.querySelector('input[type="checkbox"]').id = controlData.assetID + '-checkbox';

            let isSelected = window.selectedAssets.some((selectedControl) => {
                return (
                    selectedControl.assetID === controlData.assetID
                );
            });

            // If so, make checkbox as checked
            if (isSelected) {
                let checkbox = controlEl.querySelector('input[type="checkbox"]');
                checkbox.checked = true;
            }

            controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
                let controlData = JSON.parse(e.target.value);

                if (!e.target.checked) {
                    // Remove this control from the list of selected controls
                    let controlIndex = window.selectedAssets.findIndex(
                        (control) => control.assetID === controlData.assetID,
                    );

                    if (controlIndex !== -1) {
                        console.log('Removing control:', controlData.assetID, 'from selected controls');
                        // Remove the control from the array
                        window.selectedAssets.splice(controlIndex, 1);

                        // Update any other relevant data structures or UI elements here
                    }
                } else {
                    // Add this control to the list of selected controls
                    console.log('Add control:', controlData.assetID, 'to selected controls');
                    window.selectedAssets.push(controlData);
                }
            });

            controlEl
                .querySelector('input[type="checkbox"]')
                .parentElement.setAttribute('for', controlData.assetID + '-checkbox');

            controlEl.querySelector('.meta-standard').textContent = "Asset";
            controlEl.querySelector('.meta-id').textContent = controlData.assetID;
            controlEl.querySelector('.control-name').textContent = controlData.assetDesc.replaceAll(
                'Acme Inc',
                window.companyName,
            );
            controlEl.querySelector('.control-description').textContent = "Date added: " + new Date(controlData.dateAdded).toDateString();

            document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
        }

        removeDuplicateControls();
    } else if (mode === "risks") {
        if (query.length < 1 || query === '' || query === ' ') {
            console.log('Nothing to search, populating existing controls');
            window.populateExistingControls(true, "#selectControlsModal", "risks");
            removeDuplicateControls();
            return;
        }

        console.log('Continued');

        const result = searchObject(window.risks, query, acceptedKeys);
        console.log(`Search Filters: `, acceptedKeys);
        console.log(`Searched risks: ${query}`);
        console.log(`Search result:`, result);

        document.querySelector(`${modalID} #controls-list`).innerHTML = '';

        for (const control in result) {
            let controlData = result[control];
            let controlEl = window.controlElementTemplate.cloneNode(true);

            // if any key in controlData is empty, skip this control
            let hasEmptyKeys = false;
            for (const key in controlData) {
                if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                    hasEmptyKeys = true;
                    break;
                } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                }
            }

            // if (hasEmptyKeys) {
            //   continue;
            // }

            controlEl.removeAttribute('style');

            controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
            controlEl.querySelector('input[type="checkbox"]').id = controlData.riskID + '-checkbox';

            let isSelected = window.selectedRisks.some((selectedControl) => {
                return (
                    selectedControl.riskID === controlData.riskID
                );
            });

            // If so, make checkbox as checked
            if (isSelected) {
                let checkbox = controlEl.querySelector('input[type="checkbox"]');
                checkbox.checked = true;
            }

            controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
                let controlData = JSON.parse(e.target.value);

                if (!e.target.checked) {
                    // Remove this control from the list of selected controls
                    let controlIndex = window.selectedRisks.findIndex(
                        (control) => control.riskID === controlData.riskID,
                    );

                    if (controlIndex !== -1) {
                        console.log('Removing control:', controlData.riskID, 'from selected controls');
                        // Remove the control from the array
                        window.selectedRisks.splice(controlIndex, 1);

                        // Update any other relevant data structures or UI elements here
                    }
                } else {
                    // Add this control to the list of selected controls
                    console.log('Add control:', controlData.riskID, 'to selected controls');
                    window.selectedRisks.push(controlData);
                }
            });

            controlEl
                .querySelector('input[type="checkbox"]')
                .parentElement.setAttribute('for', controlData.riskID + '-checkbox');

            controlEl.querySelector('.meta-standard').textContent = "Risk";
            controlEl.querySelector('.meta-id').textContent = controlData.riskID;
            controlEl.querySelector('.control-name').textContent = controlData.risk.replaceAll(
                'Acme Inc',
                window.companyName,
            );
            controlEl.querySelector('.control-description').textContent = "Date added: " + new Date(controlData.dateAdded).toDateString();

            document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
        }

        removeDuplicateControls();
    } else if (mode === "ismsObjectives") {
        if (query.length < 1 || query === '' || query === ' ') {
            console.log('Nothing to search, populating existing controls');
            window.populateExistingControls(true, "#selectControlsModal", "ismsObjectives");
            removeDuplicateControls();
            return;
        }

        console.log('Continued');

        const result = searchObject(window.ismsObjectives, query, acceptedKeys);
        console.log(`Search Filters: `, acceptedKeys);
        console.log(`Searched ISMS Objectives: ${query}`);
        console.log(`Search result:`, result);

        document.querySelector(`${modalID} #controls-list`).innerHTML = '';

        for (const control in result) {
            let controlData = result[control];
            let controlEl = window.controlElementTemplate.cloneNode(true);

            // if any key in controlData is empty, skip this control
            let hasEmptyKeys = false;
            for (const key in controlData) {
                if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                    hasEmptyKeys = true;
                    break;
                } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                }
            }

            // if (hasEmptyKeys) {
            //   continue;
            // }

            controlEl.removeAttribute('style');

            controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
            controlEl.querySelector('input[type="checkbox"]').id = controlData.id + '-checkbox';

            let isSelected = window.selectedISMSObjectives.some((selectedControl) => {
                return (
                    selectedControl.id === controlData.id
                );
            });

            // If so, make checkbox as checked
            if (isSelected) {
                let checkbox = controlEl.querySelector('input[type="checkbox"]');
                checkbox.checked = true;
            }

            controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
                let controlData = JSON.parse(e.target.value);

                if (!e.target.checked) {
                    // Remove this control from the list of selected controls
                    let controlIndex = window.selectedISMSObjectives.findIndex(
                        (control) => control.id === controlData.id,
                    );

                    if (controlIndex !== -1) {
                        console.log('Removing control:', controlData.id, 'from selected controls');
                        // Remove the control from the array
                        window.selectedISMSObjectives.splice(controlIndex, 1);

                        // Update any other relevant data structures or UI elements here
                    }
                } else {
                    // Add this control to the list of selected controls
                    console.log('Add control:', controlData.id, 'to selected controls');
                    window.selectedISMSObjectives.push(controlData);
                }
            });

            controlEl
                .querySelector('input[type="checkbox"]')
                .parentElement.setAttribute('for', controlData.id + '-checkbox');

            controlEl.querySelector('.meta-standard').textContent = "ISMS Objective";
            controlEl.querySelector('.meta-id').textContent = controlData.id;
            controlEl.querySelector('.control-name').textContent = controlData.objectiveText.replaceAll(
                'Acme Inc',
                window.companyName,
            );
            controlEl.querySelector('.control-description').textContent = "Target date: " + new Date(controlData.objectiveTargetDate).toDateString();

            document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
        }

        removeDuplicateControls();
    } else if (mode === "legalObligations") {
        if (query.length < 1 || query === '' || query === ' ') {
            console.log('Nothing to search, populating existing controls');
            window.populateExistingControls(true, "#selectControlsModal", "legalObligations");
            removeDuplicateControls();
            return;
        }

        console.log('Continued');

        const result = searchObject(window.legalObligations, query, acceptedKeys);
        console.log(`Search Filters: `, acceptedKeys);
        console.log(`Searched legal obligations: ${query}`);
        console.log(`Search result:`, result);

        document.querySelector(`${modalID} #controls-list`).innerHTML = '';

        for (const control in result) {
            let controlData = result[control];
            let controlEl = window.controlElementTemplate.cloneNode(true);

            // if any key in controlData is empty, skip this control
            let hasEmptyKeys = false;
            for (const key in controlData) {
                if (typeof controlData[key] === 'object' && Object.keys(controlData[key]).length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'string' && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                } else if (typeof controlData[key] === 'undefined' && controlData[key] === null) {
                    hasEmptyKeys = true;
                    break;
                } else if (Array.isArray(controlData[key]) && controlData[key].length === 0) {
                    hasEmptyKeys = true;
                    break;
                }
            }

            // if (hasEmptyKeys) {
            //   continue;
            // }

            controlEl.removeAttribute('style');

            controlEl.querySelector('input[type="checkbox"]').value = JSON.stringify(controlData);
            controlEl.querySelector('input[type="checkbox"]').id = controlData.id + '-checkbox';

            let isSelected = window.selectedLegalObligations.some((selectedControl) => {
                return (
                    selectedControl.id === controlData.id
                );
            });

            // If so, make checkbox as checked
            if (isSelected) {
                let checkbox = controlEl.querySelector('input[type="checkbox"]');
                checkbox.checked = true;
            }

            controlEl.querySelector('input[type="checkbox"]').addEventListener('change', (e) => {
                let controlData = JSON.parse(e.target.value);

                if (!e.target.checked) {
                    // Remove this control from the list of selected controls
                    let controlIndex = window.selectedLegalObligations.findIndex(
                        (control) => control.id === controlData.id,
                    );

                    if (controlIndex !== -1) {
                        console.log('Removing control:', controlData.id, 'from selected controls');
                        // Remove the control from the array
                        window.selectedLegalObligations.splice(controlIndex, 1);

                        // Update any other relevant data structures or UI elements here
                    }
                } else {
                    // Add this control to the list of selected controls
                    console.log('Add control:', controlData.id, 'to selected controls');
                    window.selectedLegalObligations.push(controlData);
                }
            });

            controlEl
                .querySelector('input[type="checkbox"]')
                .parentElement.setAttribute('for', controlData.id + '-checkbox');

            controlEl.querySelector('.meta-standard').textContent = "Legal Obligation";
            controlEl.querySelector('.meta-id').textContent = controlData.id;
            controlEl.querySelector('.control-name').textContent = controlData.requirement.replaceAll(
                'Acme Inc',
                window.companyName,
            );
            controlEl.querySelector('.control-description').textContent = "Date added: " + new Date(controlData.dateAdded).toDateString();

            document.querySelector(`${modalID} #controls-list`).appendChild(controlEl);
        }

        removeDuplicateControls();
    }
};

window.storeSelectedControls = (mode = "controls") => {
    let controlsToAdd = [];
    let cI = 0;
    if (mode === "controls") {
        document.querySelector(`${window.modalBeforeControlSelector} .controlSelectorButton`).value = '';
        for (let i = 0; i < window.selectedControls.length; i++) {
            const control = window.selectedControls[i];
            controlsToAdd.push(control);
            let currentIDs = document.querySelector(`${window.modalBeforeControlSelector} .controlSelectorButton`)
                .value.split(', ');
            if (!currentIDs.includes(control.controlID)) {
                currentIDs.push(window?.useFrameworksInsteadOfControls ? control.standard : control.controlID);
            }

            document.querySelector(`${window.modalBeforeControlSelector} .controlSelectorButton`).value =
                currentIDs.join(', ').replace(/^[\s,]+/, '');
        }

        // Merge controlsToAdd with window.selectedControls and filter out duplicates
        window.selectedControls = mergeAndFilterControls(window.selectedControls, controlsToAdd);

        console.log('Selected Controls:', window.selectedControls);
    } else if (mode === "assets") {
        document.querySelector(`${window.modalBeforeControlSelector} .assetsSelectorButton`).value = '';
        for (let i = 0; i < window.selectedAssets.length; i++) {
            const control = window.selectedAssets[i];
            controlsToAdd.push(control);
            let currentIDs = document.querySelector(`${window.modalBeforeControlSelector} .assetsSelectorButton`)
                .value.split(', ');
            if (!currentIDs.includes(control.assetID)) {
                currentIDs.push(control.assetID);
            }

            document.querySelector(`${window.modalBeforeControlSelector} .assetsSelectorButton`).value =
                currentIDs.join(', ').replace(/^[\s,]+/, '');
        }

        // Merge controlsToAdd with window.selectedControls and filter out duplicates
        window.selectedAssets = mergeAndFilterAssets(window.selectedAssets, controlsToAdd);

        console.log('Selected assets:', window.selectedAssets);
    } else if (mode === "risks") {
        document.querySelector(`${window.modalBeforeControlSelector} .risksSelectorButton`).value = '';
        for (let i = 0; i < window.selectedRisks.length; i++) {
            const control = window.selectedRisks[i];
            controlsToAdd.push(control);
            let currentIDs = document.querySelector(`${window.modalBeforeControlSelector} .risksSelectorButton`)
                .value.split(', ');
            if (!currentIDs.includes(control.riskID)) {
                currentIDs.push(control.riskID);
            }

            document.querySelector(`${window.modalBeforeControlSelector} .risksSelectorButton`).value =
                currentIDs.join(', ').replace(/^[\s,]+/, '');
        }

        // Merge controlsToAdd with window.selectedControls and filter out duplicates
        window.selectedRisks = mergeAndFilterRisks(window.selectedRisks, controlsToAdd);

        console.log('Selected risks:', window.selectedRisks);
    } else if (mode === "legalObligations") {
        document.querySelector(`${window.modalBeforeControlSelector} .legalObligationsSelectorButton`).value = '';
        for (let i = 0; i < window.selectedLegalObligations.length; i++) {
            const control = window.selectedLegalObligations[i];
            controlsToAdd.push(control);
            let currentIDs = document.querySelector(`${window.modalBeforeControlSelector} .legalObligationsSelectorButton`)
                .value.split(', ');
            if (!currentIDs.includes(control.id)) {
                currentIDs.push(control.id);
            }

            document.querySelector(`${window.modalBeforeControlSelector} .legalObligationsSelectorButton`).value =
                currentIDs.join(', ').replace(/^[\s,]+/, '');
        }

        // Merge controlsToAdd with window.selectedControls and filter out duplicates
        window.selectedLegalObligations = mergeAndFilterLegalObligations(window.selectedLegalObligations, controlsToAdd);

        console.log('Selected legal obligations:', window.selectedLegalObligations);
    } else if (mode === "ismsObjectives") {
        document.querySelector(`${window.modalBeforeControlSelector} .ismsObjectivesSelectorButton`).value = '';
        for (let i = 0; i < window.selectedISMSObjectives.length; i++) {
            const control = window.selectedISMSObjectives[i];
            controlsToAdd.push(control);
            let currentIDs = document.querySelector(`${window.modalBeforeControlSelector} .ismsObjectivesSelectorButton`)
                .value.split(', ');
            if (!currentIDs.includes(control.id)) {
                currentIDs.push(control.id);
            }

            document.querySelector(`${window.modalBeforeControlSelector} .ismsObjectivesSelectorButton`).value =
                currentIDs.join(', ').replace(/^[\s,]+/, '');
        }

        // Merge controlsToAdd with window.selectedControls and filter out duplicates
        window.selectedISMSObjectives = mergeAndFilterISMSObjectives(window.selectedISMSObjectives, controlsToAdd);

        console.log('Selected ISMS objectives:', window.selectedISMSObjectives);
    }
};

function mergeAndFilterControls(existingControls, newControls) {
    const controlIds = new Set(existingControls.map((control) => control?.controlID ?? control));
    const mergedControls = existingControls.slice(); // Create a copy of existingControls

    for (const control of newControls) {
        if (!controlIds.has(control.controlID)) {
            mergedControls.push(control);
            controlIds.add(control.controlID);
        } else {
            // Update existing control with the new data
            const existingIndex = mergedControls.findIndex((c) => c.controlID === control.controlID);
            mergedControls[existingIndex] = { ...mergedControls[existingIndex], ...control };
        }
    }

    return mergedControls;
}

function mergeAndFilterAssets(existingAssets, newAssets) {
    const controlIds = new Set(existingAssets.map((control) => control?.assetID ?? control));
    const mergedControls = existingAssets.slice(); // Create a copy of existingControls

    for (const control of newAssets) {
        if (!controlIds.has(control.assetID)) {
            mergedControls.push(control);
            controlIds.add(control.assetID);
        } else {
            // Update existing control with the new data
            const existingIndex = mergedControls.findIndex((c) => c.assetID === control.assetID);
            mergedControls[existingIndex] = { ...mergedControls[existingIndex], ...control };
        }
    }

    return mergedControls;
}

function mergeAndFilterRisks(existingRisks, newRisks) {
    const controlIds = new Set(existingRisks.map((control) => control?.riskID ?? control));
    const mergedControls = existingRisks.slice(); // Create a copy of existingControls

    for (const control of newRisks) {
        if (!controlIds.has(control.riskID)) {
            mergedControls.push(control);
            controlIds.add(control.riskID);
        } else {
            // Update existing control with the new data
            const existingIndex = mergedControls.findIndex((c) => c.riskID === control.riskID);
            mergedControls[existingIndex] = { ...mergedControls[existingIndex], ...control };
        }
    }

    return mergedControls;
}

function mergeAndFilterISMSObjectives(existingISMSObjectives, newISMSObjectives) {
    const controlIds = new Set(existingISMSObjectives.map((control) => control?.id ?? control));
    const mergedControls = existingISMSObjectives.slice(); // Create a copy of existingControls

    for (const control of newISMSObjectives) {
        if (!controlIds.has(control.id)) {
            mergedControls.push(control);
            controlIds.add(control.id);
        } else {
            // Update existing control with the new data
            const existingIndex = mergedControls.findIndex((c) => c.id === control.id);
            mergedControls[existingIndex] = { ...mergedControls[existingIndex], ...control };
        }
    }

    return mergedControls;
}

function mergeAndFilterLegalObligations(existingLegalObligations, newLegalObligations) {
    const controlIds = new Set(existingLegalObligations.map((control) => control?.id ?? control));
    const mergedControls = existingLegalObligations.slice(); // Create a copy of existingControls

    for (const control of newLegalObligations) {
        if (!controlIds.has(control.id)) {
            mergedControls.push(control);
            controlIds.add(control.id);
        } else {
            // Update existing control with the new data
            const existingIndex = mergedControls.findIndex((c) => c.id === control.id);
            mergedControls[existingIndex] = { ...mergedControls[existingIndex], ...control };
        }
    }

    return mergedControls;
}

const initialiseSelectorModal = async (modalID = "#selectControlsModal") => {
    // Insert the control selector template into the DOM
    const newControlSelectorTemplate = controlSelectorTemplate.toString().replaceAll("selectControlsModal", modalID.split('#')[1]);
    document.body.insertAdjacentHTML('beforeend', newControlSelectorTemplate);
    const standardKeys = Object.keys(filters.standards);
    await waitForSelector('#selectControlsModal', { timeout: 30000 });
    const controlSelectorModal = document.querySelector(modalID);
    if (controlSelectorModal === null) throw new Error('Control selector modal not found');
    const standardBtnsDiv = controlSelectorModal.querySelector('#controlSelectorStandardList');
    console.warn(controlSelectorModal, controlSelectorModal.querySelector('#controlElementTemplate'))
    await waitForSelector('#selectControlsModal #controlElementTemplate', { timeout: 30000 });
    window.controlElementTemplate = controlSelectorModal.querySelector('#controlElementTemplate').cloneNode(true);

    await loadData();

    for (const standardKey of standardKeys) {
        const standardLi = document.createElement('li');
        const standardBtn = document.createElement('button');
        standardBtn.textContent = standardKey;
        if (standardKey === 'ISO 27001') {
            standardLi.classList.add('nav-item', 'iso', 'isoActive');
        } else if (standardKey === 'SOC 2' || standardKey === 'NIST 800-53' || standardKey === 'ISM') {
            standardLi.classList.add('nav-item', 'iso');
            if (standardKey === "ISM") {
                const complianceModulesData = await db.collection("customers").doc(window.companyID).collection('companySettings').doc('complianceModules').get();
                if (complianceModulesData.data()?.ism?.Enabled !== true) {
                    continue;
                }
            }
        } else if (standardKey === 'Essential 8') {
            standardLi.classList.add('nav-item');
        } else if (standardKey === 'Privacy Act') {
            standardLi.classList.add('nav-item', 'paActive');
        } else if (standardKey === 'DISP') {
            standardLi.classList.add('nav-item');
            const complianceModulesData = await db.collection("customers").doc(window.companyID).collection('companySettings').doc('complianceModules').get();
            if (complianceModulesData.data()?.DISP?.Enabled !== true) {
                continue;
            }
        }
        standardBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.target.classList.toggle('active')
        })
        standardBtn.classList.add('btn', 'btn-outline-primary');
        standardLi.appendChild(standardBtn);
        standardBtnsDiv.appendChild(standardLi);
        standardBtnsDiv.addEventListener('click', (e) => {
            e.preventDefault();
            let filter = e.target.innerText;

            if (e.target.classList.contains('active')) {
                if (window.acceptedKeyFilters === undefined) {
                    window.acceptedKeyFilters = [];
                    window.acceptedKeyFilters.push(filter);
                } else {
                    if (!window.acceptedKeyFilters.includes(filter)) {
                        window.acceptedKeyFilters.push(filter);
                    }
                }
            } else {
                if (window.acceptedKeyFilters.includes(filter)) {
                    let index = window.acceptedKeyFilters.indexOf(filter);
                    window.acceptedKeyFilters.splice(index, 1);
                }
            }

            let query = document.querySelector(`${modalID} .searchbar`).value;

            searchAndPopulate(query, window.acceptedKeyFilters);
            removeDuplicateControls();
        });
    }

    if (window.customerPlan?.toLowerCase() === 'starter') {
        document.querySelectorAll(`${modalID} .paActive`).forEach((select) => {
            select.querySelector(".btn").classList.add('active');
        })
        document.querySelectorAll(`${modalID} .iso`).forEach((select) => {
            select.style.display = 'none';
        });
    } else {
        document.querySelectorAll(`${modalID} .isoActive`).forEach((select) => {
            select.querySelector(".btn").classList.add('active');
        })
    }

}

const loadSelectorModal = async (selectControlsModalID = "#selectControlsModal", currentModalID, presetFilter = null, forRiskRegister = false, riskText = null, mode = "controls") => {
    window.acceptedKeyFilters = []
    document.querySelector(selectControlsModalID).classList.add('show');
    let modal = new coreui.Modal(`${selectControlsModalID}`, {
        backdrop: 'static',
        keyboard: false,
    });
    window.activeModal = modal;
    modal.toggle();
    document.querySelector(currentModalID).style.display = 'none';
    window.modalBeforeControlSelector = currentModalID;
    document.querySelector(selectControlsModalID).querySelector("#selectorModalCloseBtn").addEventListener('click', async () => {
        // Modification starts here
        let hasUnsavedChanges = false;

        if (mode === "controls") {
            // Compare current selections with initial selections
            if (JSON.stringify(window.selectedControls) !== JSON.stringify(window.initialSelectedControls)) {
                hasUnsavedChanges = true;
            }
        } else if (mode === "assets") {
            if (JSON.stringify(window.selectedAssets) !== JSON.stringify(window.initialSelectedAssets)) {
                hasUnsavedChanges = true;
            }
        } else if (mode === "risks") {
            if (JSON.stringify(window.selectedRisks) !== JSON.stringify(window.initialSelectedRisks)) {
                hasUnsavedChanges = true;
            }
        } else if (mode === "legalObligations") {
            if (JSON.stringify(window.selectedLegalObligations) !== JSON.stringify(window.initialSelectedLegalObligations)) {
                hasUnsavedChanges = true;
            }
        } else if (mode === "ismsObjectives") {
            if (JSON.stringify(window.selectedISMSObjectives) !== JSON.stringify(window.initialSelectedISMSObjectives)) {
                hasUnsavedChanges = true;
            }
        }

        if (hasUnsavedChanges) {
            // Show warning
            if (confirm("You have unsaved changes. Are you sure you want to close without saving?")) {
                // User confirmed, proceed to close
                closeSelectorModal(mode, false);
            } else {
                // User canceled, do not close
                return;
            }
        } else {
            // No unsaved changes, proceed to close
            closeSelectorModal(mode, false);
        }
    });
    document.querySelector(selectControlsModalID).querySelector("#selectorModalSaveBtn").addEventListener('click', () => {
        closeSelectorModal(mode, true);
    });
    document.querySelector(`${selectControlsModalID} .searchbar`).addEventListener('keyup', (e) => {
        console.log('Search Value', e.target.value)
        window.searchAndPopulate(e.target.value, window.acceptedKeyFilters || [], selectControlsModalID, mode)
    })
    if (mode === "controls") {
        window.initialSelectedControls = JSON.parse(JSON.stringify(window.selectedControls || []));
        document.querySelector(selectControlsModalID).querySelector("#controlSelectorStandardList").parentElement.style.display = "flex";
        document.querySelector(selectControlsModalID).querySelector("#modalTitle").innerHTML = `Related Controls
                            <div class="sub" id="modalSubtitle">
                                Select the controls that relate to this issue
                            </div>`;
        document.querySelector(selectControlsModalID).querySelector("#secondaryTitle").innerText = "Select Controls";
        document.querySelectorAll(`${selectControlsModalID} .page-buttons .btn`).forEach((btn) => {
            btn.classList.remove('active');
        });

        console.log(window.customerPlan)
        if (window.customerPlan?.toLowerCase() === 'starter') {
            document.querySelectorAll(`${selectControlsModalID} .paActive`).forEach((select) => {
                select.querySelector(".btn").classList.add('active');
            })
        } else {
            document.querySelectorAll(`${selectControlsModalID} .isoActive`).forEach((select) => {
                select.querySelector(".btn").classList.add('active');
            })
        }

        document.querySelector(`${selectControlsModalID} .searchbar`).value = '';
        document.querySelector(`${selectControlsModalID} .searchbar`).setAttribute('placeholder', 'Search Controls');

        if (forRiskRegister === true) {
            document.querySelector(`${selectControlsModalID} .text-block`).innerText = riskText;
            document.querySelector(`${selectControlsModalID} #riskDescriptionDiv`).style.display = "block";
        }

        window.selectedControls =
            window.selectedControls !== null
                ? window.selectedControls
                : [];

        window.populateExistingControls(true, "#selectControlsModal", "controls");
        removeDuplicateControls();
    } else if (mode === "assets") {
        window.initialSelectedAssets = JSON.parse(JSON.stringify(window.selectedAssets || []));
        document.querySelector(selectControlsModalID).querySelector("#controlSelectorStandardList").parentElement.style.display = "none";
        document.querySelector(selectControlsModalID).querySelector("#modalTitle").innerHTML = `Assets Associated
                            <div class="sub" id="modalSubtitle">
                                Select the assets that relate to this issue
                            </div>`;
        document.querySelector(selectControlsModalID).querySelector("#secondaryTitle").innerText = "Select Assets";
        document.querySelectorAll(`${selectControlsModalID} .page-buttons .btn`).forEach((btn) => {
            btn.classList.remove('active');
        });

        document.querySelector(`${selectControlsModalID} .searchbar`).value = '';
        document.querySelector(`${selectControlsModalID} .searchbar`).setAttribute('placeholder', 'Search Assets');

        if (forRiskRegister === true) {
            document.querySelector(`${selectControlsModalID} .text-block`).innerText = riskText;
            document.querySelector(`${selectControlsModalID} #riskDescriptionDiv`).style.display = "block";
        }

        window.selectedAssets =
            window.selectedAssets !== null
                ? window.selectedAssets
                : [];

        window.populateExistingControls(true, "#selectControlsModal", "assets");
        removeDuplicateControls();
    } else if (mode === "risks") {
        window.initialSelectedRisks = JSON.parse(JSON.stringify(window.selectedRisks ?? []));
        document.querySelector(selectControlsModalID).querySelector("#controlSelectorStandardList").parentElement.style.display = "none";
        document.querySelector(selectControlsModalID).querySelector("#modalTitle").innerHTML = `Related Risks
                            <div class="sub" id="modalSubtitle">
                                Select the risks that relate to this issue.
                            </div>`;
        document.querySelector(selectControlsModalID).querySelector("#secondaryTitle").innerText = "Select Risks";
        document.querySelectorAll(`${selectControlsModalID} .page-buttons .btn`).forEach((btn) => {
            btn.classList.remove('active');
        });

        document.querySelector(`${selectControlsModalID} .searchbar`).value = '';
        document.querySelector(`${selectControlsModalID} .searchbar`).setAttribute('placeholder', 'Search Risks');

        if (forRiskRegister === true) {
            document.querySelector(`${selectControlsModalID} .text-block`).innerText = riskText;
            document.querySelector(`${selectControlsModalID} #riskDescriptionDiv`).style.display = "block";
        }

        window.selectedRisks =
            window.selectedRisks !== null
                ? window.selectedRisks
                : [];

        window.populateExistingControls(true, "#selectControlsModal", "risks");
        removeDuplicateControls();
    } else if (mode === "legalObligations") {
        window.initialSelectedLegalObligations = JSON.parse(JSON.stringify(window.selectedLegalObligations || []));
        document.querySelector(selectControlsModalID).querySelector("#controlSelectorStandardList").parentElement.style.display = "none";
        document.querySelector(selectControlsModalID).querySelector("#modalTitle").innerHTML = `Legal Obligations Associated
                            <div class="sub" id="modalSubtitle">
                                Select the legal obligations that relate to this issue
                            </div>`;
        document.querySelector(selectControlsModalID).querySelector("#secondaryTitle").innerText = "Select Legal Obligations";
        document.querySelectorAll(`${selectControlsModalID} .page-buttons .btn`).forEach((btn) => {
            btn.classList.remove('active');
        });

        document.querySelector(`${selectControlsModalID} .searchbar`).value = '';
        document.querySelector(`${selectControlsModalID} .searchbar`).setAttribute('placeholder', 'Search Legal Obligations');

        if (forRiskRegister === true) {
            document.querySelector(`${selectControlsModalID} .text-block`).innerText = riskText;
            document.querySelector(`${selectControlsModalID} #riskDescriptionDiv`).style.display = "block";
        }

        window.selectedLegalObligations =
            window.selectedLegalObligations !== null
                ? window.selectedLegalObligations
                : [];

        window.populateExistingControls(true, "#selectControlsModal", "legalObligations");
        removeDuplicateControls();
    } else if (mode === "ismsObjectives") {
        window.initialSelectedISMSObjectives = JSON.parse(JSON.stringify(window.selectedISMSObjectives || []));
        document.querySelector(selectControlsModalID).querySelector("#controlSelectorStandardList").parentElement.style.display = "none";
        document.querySelector(selectControlsModalID).querySelector("#modalTitle").innerHTML = `ISMS Objectives Associated
                            <div class="sub" id="modalSubtitle">
                                Select the ISMS objectives that relate to this issue
                            </div>`;
        document.querySelector(selectControlsModalID).querySelector("#secondaryTitle").innerText = "Select ISMS Objectives";
        document.querySelectorAll(`${selectControlsModalID} .page-buttons .btn`).forEach((btn) => {
            btn.classList.remove('active');
        });

        document.querySelector(`${selectControlsModalID} .searchbar`).value = '';
        document.querySelector(`${selectControlsModalID} .searchbar`).setAttribute('placeholder', 'Search Objectives');

        if (forRiskRegister === true) {
            document.querySelector(`${selectControlsModalID} .text-block`).innerText = riskText;
            document.querySelector(`${selectControlsModalID} #riskDescriptionDiv`).style.display = "block";
        }

        window.selectedISMSObjectives =
            window.selectedISMSObjectives !== null
                ? window.selectedISMSObjectives
                : [];

        window.populateExistingControls(true, "#selectControlsModal", "ismsObjectives");
        removeDuplicateControls();
    }
    if (presetFilter) {
        const selectControlsModal = document.querySelector(selectControlsModalID);
        if (selectControlsModal) {
            const filterBtn = Array.from(selectControlsModal.querySelectorAll('button')).find(btn => btn.innerText.trim() === presetFilter);
            if (filterBtn) {
                filterBtn.click();
            }
        }
    }
}

// const closeSelectorModal = async (mode = "controls", save = true) => {
//     if (save === true) {
//         window.storeSelectedControls(mode);
//     }
//     window.activeModal.toggle();
//     document.querySelector(window.modalBeforeControlSelector).style.display = 'block';
// }

const closeSelectorModal = async (mode = "controls", save = true) => {
    if (save === true) {
        window.storeSelectedControls(mode);
    } else {
        // Reset the selections to the initial state if not saving
        if (mode === "controls") {
            window.selectedControls = JSON.parse(JSON.stringify(window.initialSelectedControls));
        } else if (mode === "assets") {
            window.selectedAssets = JSON.parse(JSON.stringify(window.initialSelectedAssets));
        } else if (mode === "risks") {
            window.selectedRisks = JSON.parse(JSON.stringify(window.initialSelectedRisks));
        } else if (mode === "legalObligations") {
            window.selectedLegalObligations = JSON.parse(JSON.stringify(window.initialSelectedLegalObligations));
        } else if (mode === "ismsObjectives") {
            window.selectedISMSObjectives = JSON.parse(JSON.stringify(window.initialSelectedISMSObjectives));
        }
    }
    window.activeModal.toggle();
    document.querySelector(window.modalBeforeControlSelector).style.display = 'block';
}

window.closeSelectorModal = closeSelectorModal;

export { initialiseSelectorModal, loadSelectorModal };
